import { useState, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { ChevronDown, RotateCcw, Plus, X, Info, Trash2 } from 'lucide-react';
import { getOccurrences, updateOccurrenceAmounts } from '@/lib/api';
import { toast } from 'sonner';
import { formatCurrency as fmtCurrency, getCurrencySymbol, useCurrency } from '@/lib/currency';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const RECURRENCE_OPTIONS = [
  { value: 'one_time', label: 'One Time' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'bi_weekly', label: 'Bi-Weekly' },
  { value: 'semi_monthly', label: 'Semi-Monthly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'annual', label: 'Annual' },
  { value: 'custom', label: 'Custom Interval' },
  { value: 'none', label: 'None (Sporadic - Custom Dates)' },
];

const DAY_OPTIONS = [
  { value: 0, label: 'Monday' },
  { value: 1, label: 'Tuesday' },
  { value: 2, label: 'Wednesday' },
  { value: 3, label: 'Thursday' },
  { value: 4, label: 'Friday' },
  { value: 5, label: 'Saturday' },
  { value: 6, label: 'Sunday' },
];

const getToday = () => {
  return new Date().toISOString().split('T')[0];
};

const formatDate = (dateStr) => {
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

// Helper to get month key from date string (for Year 1/Year 2 mapping)
function getMonthKey(dateStr) {
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', { month: 'short' });
}

// Helper to determine if an occurrence is in Year 1 or Year 2
// Also assigns occurrence index for bi-weekly/weekly looping
function splitOccurrencesByYear(occurrences, recurrenceType) {
  if (!occurrences || occurrences.length === 0) return { year1: [], year2: [], occurrencesPerYear: 0 };
  
  // Sort occurrences by date
  const sorted = [...occurrences].sort((a, b) => a.date.localeCompare(b.date));
  
  // Find the first occurrence date to determine the year boundary
  const firstDate = new Date(sorted[0].date + 'T00:00:00');
  const year1End = new Date(firstDate);
  year1End.setFullYear(year1End.getFullYear() + 1);
  
  const year1 = [];
  const year2 = [];
  
  sorted.forEach((occ, index) => {
    const occDate = new Date(occ.date + 'T00:00:00');
    // Add occurrence index for bi-weekly/weekly looping
    const occWithIndex = { ...occ, occurrenceIndex: index };
    
    if (occDate < year1End) {
      year1.push(occWithIndex);
    } else {
      year2.push(occWithIndex);
    }
  });
  
  // For bi-weekly/weekly looping, use actual Year 1 count as the offset
  // This ensures Year 2's nth occurrence maps to Year 1's nth occurrence
  // regardless of how many actual occurrences fall within each year boundary
  const occurrencesPerYear = year1.length;
  
  return { year1, year2, occurrencesPerYear };
}

// Helper to get corresponding Year 1 date for a Year 2 date (for auto-loop)
// Uses different logic for bi-weekly/weekly vs monthly patterns
function getYear1DateForYear2(year2Occ, year1Occurrences, recurrenceType, occurrencesPerYear) {
  // For bi-weekly and weekly: loop by occurrence number
  if (recurrenceType === 'bi_weekly' || recurrenceType === 'weekly') {
    // Calculate Year 1 occurrence index: Year 2 index - occurrences per year
    const y1Index = year2Occ.occurrenceIndex - occurrencesPerYear;
    
    // Find Year 1 occurrence with that index
    const match = year1Occurrences.find(occ => occ.occurrenceIndex === y1Index);
    return match ? match.date : null;
  }
  
  // For monthly/quarterly/annual: loop by calendar month
  const y2Date = new Date(year2Occ.date + 'T00:00:00');
  const y2Month = y2Date.getMonth();
  
  // Find the Year 1 occurrence with the same month
  const match = year1Occurrences.find(occ => {
    const occDate = new Date(occ.date + 'T00:00:00');
    return occDate.getMonth() === y2Month;
  });
  
  return match ? match.date : null;
}

// Format a Date as "YYYY-MM-DD" using LOCAL components.
// toISOString() converts to UTC and can shift the date by a day in
// positive-offset timezones, so we never use it for date storage.
function formatLocalDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// Convert ISO weekday (Mon=0..Sun=6 — used by DAY_OPTIONS / backend)
// to JS getDay() weekday (Sun=0..Sat=6).
const isoDowToJs = (iso) => (iso + 1) % 7;
// Convert JS getDay() to ISO weekday.
const jsDowToIso = (js) => (js + 6) % 7;

// Generate future occurrence dates client-side for new transactions
function generateOccurrenceDates(formData, maxDays) {
  const occurrences = [];
  const startDate = new Date(formData.start_date + 'T00:00:00');
  const endDate = formData.end_date ? new Date(formData.end_date + 'T00:00:00') : null;
  const maxDate = new Date();
  maxDate.setDate(maxDate.getDate() + maxDays);
  
  const effectiveEnd = endDate && endDate < maxDate ? endDate : maxDate;
  
  if (formData.recurrence_type === 'one_time') {
    if (startDate <= effectiveEnd) {
      occurrences.push(formatLocalDate(startDate));
    }
    return occurrences;
  }
  
  let current = new Date(startDate);
  let count = 0;
  const maxCount = 200; // Safety limit
  
  // For monthly/quarterly/annual, adjust the start to use day_of_month if specified
  if (['monthly', 'quarterly', 'annual'].includes(formData.recurrence_type) && formData.day_of_month) {
    const targetDay = parseInt(formData.day_of_month, 10);
    const currentDay = current.getDate();
    
    // Set to the target day of month
    current.setDate(targetDay);
    
    // Handle month overflow (e.g., setting day 31 in a 30-day month)
    if (current.getDate() !== targetDay) {
      // Month overflowed, go to last day of previous month
      current.setDate(0);
    }
    
    // If the calculated date is before the start date, move to next occurrence
    if (current < startDate) {
      if (formData.recurrence_type === 'monthly') {
        current.setMonth(current.getMonth() + 1);
        current.setDate(targetDay);
        if (current.getDate() !== targetDay) {
          current.setDate(0);
        }
      } else if (formData.recurrence_type === 'quarterly') {
        current.setMonth(current.getMonth() + 3);
        current.setDate(targetDay);
        if (current.getDate() !== targetDay) {
          current.setDate(0);
        }
      } else if (formData.recurrence_type === 'annual') {
        current.setFullYear(current.getFullYear() + 1);
        current.setDate(targetDay);
        if (current.getDate() !== targetDay) {
          current.setDate(0);
        }
      }
    }
  }
  
  // For weekly/bi-weekly, adjust to the correct day of week if specified.
  // formData.day_of_week is stored in ISO convention (Mon=0..Sun=6) to
  // match the backend / DAY_OPTIONS, but JS getDay() uses Sun=0..Sat=6.
  if (['weekly', 'bi_weekly'].includes(formData.recurrence_type) && formData.day_of_week !== null && formData.day_of_week !== undefined) {
    const targetDow = isoDowToJs(parseInt(formData.day_of_week, 10));
    const currentDow = current.getDay();
    let daysToAdd = targetDow - currentDow;
    if (daysToAdd < 0) {
      daysToAdd += 7;
    }
    current.setDate(current.getDate() + daysToAdd);
  }
  
  while (current <= effectiveEnd && count < maxCount) {
    occurrences.push(formatLocalDate(current));
    count++;
    
    // Advance to next occurrence based on recurrence type
    if (formData.recurrence_type === 'weekly') {
      current.setDate(current.getDate() + 7);
    } else if (formData.recurrence_type === 'bi_weekly') {
      current.setDate(current.getDate() + 14);
    } else if (formData.recurrence_type === 'monthly') {
      // Move to same day next month
      const targetDay = formData.day_of_month ? parseInt(formData.day_of_month, 10) : current.getDate();
      current.setMonth(current.getMonth() + 1);
      current.setDate(targetDay);
      // Handle month overflow
      if (current.getDate() !== targetDay) {
        current.setDate(0);
      }
    } else if (formData.recurrence_type === 'semi_monthly') {
      // Handle semi-monthly (two dates per month)
      const day1 = formData.day_of_month || 1;
      const day2 = formData.second_day_of_month || 15;
      const currentDay = current.getDate();
      if (currentDay === day1) {
        current.setDate(day2);
      } else {
        current.setMonth(current.getMonth() + 1);
        current.setDate(day1);
      }
    } else if (formData.recurrence_type === 'quarterly') {
      const targetDay = formData.day_of_month ? parseInt(formData.day_of_month, 10) : current.getDate();
      current.setMonth(current.getMonth() + 3);
      current.setDate(targetDay);
      if (current.getDate() !== targetDay) {
        current.setDate(0);
      }
    } else if (formData.recurrence_type === 'annual') {
      const targetDay = formData.day_of_month ? parseInt(formData.day_of_month, 10) : current.getDate();
      current.setFullYear(current.getFullYear() + 1);
      current.setDate(targetDay);
      if (current.getDate() !== targetDay) {
        current.setDate(0);
      }
    } else if (formData.recurrence_type === 'custom') {
      current.setDate(current.getDate() + (formData.custom_interval_days || 7));
    } else {
      break;
    }
  }
  
  return occurrences;
}

export const TransactionForm = ({ open, onOpenChange, onSubmit, onDelete, initialData = null, isGuestMode = false }) => {
  const isEditing = !!initialData;
  const [currencyCode] = useCurrency();
  const formatCurrency = (v) => fmtCurrency(Math.abs(v), currencyCode);
  const currencySymbol = getCurrencySymbol(currencyCode);
  
  const [formData, setFormData] = useState({
    name: '',
    amount: '',
    is_expense: true,
    recurrence_type: 'monthly',
    start_date: getToday(),
    end_date: '',
    day_of_week: null,
    day_of_month: null,
    second_day_of_month: null,
    custom_interval_days: 7,
    is_hypothetical: false,
  });

  const [occurrences, setOccurrences] = useState([]);
  const [customAmounts, setCustomAmounts] = useState({});
  const [year2CustomAmounts, setYear2CustomAmounts] = useState({});
  const [year2Expanded, setYear2Expanded] = useState(false);
  const [year2WasCustomized, setYear2WasCustomized] = useState(false);
  const [loadingOccurrences, setLoadingOccurrences] = useState(false);
  const [occurrencesOpen, setOccurrencesOpen] = useState(false);
  
  // State for sporadic transactions (custom date/amount pairs)
  const [sporadicEntries, setSporadicEntries] = useState([]);

  // Delete-confirmation modal state — separate from the form dialog so the
  // confirmation stays visible even after the form dialog closes.
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Recurring transactions warrant a stronger warning because deleting them
  // wipes every future occurrence at once.
  const isRecurring = isEditing && initialData
    && initialData.recurrence_type !== 'one_time'
    && initialData.recurrence_type !== 'none';

  const handleConfirmDelete = () => {
    if (!initialData || !onDelete) return;
    onDelete(initialData.id);
    setShowDeleteConfirm(false);
    onOpenChange(false);
  };

  useEffect(() => {
    if (initialData) {
      const isSporadic = initialData.recurrence_type === 'none';
      setFormData({
        name: initialData.name || '',
        amount: isSporadic ? '' : (Math.abs(initialData.amount || 0).toString() || ''),
        is_expense: isSporadic ? true : (initialData.amount < 0),
        recurrence_type: initialData.recurrence_type || 'monthly',
        start_date: initialData.start_date || getToday(),
        end_date: initialData.end_date || '',
        day_of_week: initialData.day_of_week,
        day_of_month: initialData.day_of_month,
        second_day_of_month: initialData.second_day_of_month,
        custom_interval_days: initialData.custom_interval_days || 7,
        is_hypothetical: initialData.is_hypothetical || false,
      });
      
      // Load sporadic entries from custom_amounts
      if (isSporadic && initialData.custom_amounts) {
        const entries = Object.entries(initialData.custom_amounts)
          .map(([date, amount]) => ({
            date,
            amount: Math.abs(amount).toString(),
            is_expense: amount < 0
          }))
          .sort((a, b) => a.date.localeCompare(b.date));
        setSporadicEntries(entries);
      } else {
        setSporadicEntries([]);
      }
      
      // Load occurrences for editing
      if (!isSporadic && initialData.recurrence_type !== 'one_time') {
        loadOccurrences(initialData.id);
      }
    } else {
      setFormData({
        name: '',
        amount: '',
        is_expense: true,
        recurrence_type: 'monthly',
        start_date: getToday(),
        end_date: '',
        day_of_week: null,
        day_of_month: null,
        second_day_of_month: null,
        custom_interval_days: 7,
        is_hypothetical: false,
      });
      setOccurrences([]);
      setCustomAmounts({});
      setYear2CustomAmounts({});
      setYear2Expanded(false);
      setYear2WasCustomized(false);
      setSporadicEntries([]);
      setOccurrencesOpen(false);
    }
  }, [initialData, open]);

  const loadOccurrences = async (transactionId) => {
    setLoadingOccurrences(true);
    try {
      let occData = [];
      
      if (isGuestMode) {
        // For guest mode, generate occurrences client-side from initialData
        if (initialData && initialData.recurrence_type !== 'one_time') {
          const dates = generateOccurrenceDates({
            ...initialData,
            start_date: initialData.start_date,
            end_date: initialData.end_date,
          }, 730);
          occData = dates.map(function(date) {
            const customAmt = initialData.custom_amounts?.[date];
            return {
              date,
              amount: customAmt !== undefined ? customAmt : initialData.amount,
              is_custom: customAmt !== undefined,
            };
          });
        }
      } else {
        const res = await getOccurrences(transactionId, 730); // 2 years
        occData = res.data.occurrences || [];
      }
      
      setOccurrences(occData);
      
      // Split custom amounts into Year 1 and Year 2
      // Need to get recurrence type from initialData for correct looping
      const recType = initialData?.recurrence_type || 'monthly';
      const { year1, year2 } = splitOccurrencesByYear(occData, recType);
      const year1Customs = {};
      const year2Customs = {};
      let hasYear2Customs = false;
      
      occData.forEach(function(occ) {
        if (occ.is_custom) {
          // Determine if this occurrence is in Year 1 or Year 2
          const isYear2 = year2.some(y2 => y2.date === occ.date);
          if (isYear2) {
            year2Customs[occ.date] = Math.abs(occ.amount).toString();
            hasYear2Customs = true;
          } else {
            year1Customs[occ.date] = Math.abs(occ.amount).toString();
          }
        }
      });
      
      setCustomAmounts(year1Customs);
      setYear2CustomAmounts(year2Customs);
      setYear2WasCustomized(hasYear2Customs);
      setYear2Expanded(hasYear2Customs);
      
    } catch (err) {
      console.error('Failed to load occurrences:', err);
    } finally {
      setLoadingOccurrences(false);
    }
  };

  // Generate occurrences client-side for new transactions
  const generateLocalOccurrences = () => {
    if (formData.recurrence_type === 'one_time') {
      setOccurrences([]);
      return;
    }
    const dates = generateOccurrenceDates(formData, 730); // 2 years
    const occData = dates.map(function(date) {
      return { date: date, amount: parseFloat(formData.amount) || 0, is_custom: false };
    });
    setOccurrences(occData);
  };

  // Handle opening the customize section
  const handleOpenOccurrences = (isOpen) => {
    setOccurrencesOpen(isOpen);
    if (isOpen && !isEditing && occurrences.length === 0) {
      generateLocalOccurrences();
    }
  };

  // Regenerate occurrences when form data changes (for new transactions)
  useEffect(function() {
    if (!isEditing && occurrencesOpen && formData.recurrence_type !== 'one_time' && formData.recurrence_type !== 'none') {
      generateLocalOccurrences();
    }
  }, [formData.recurrence_type, formData.start_date, formData.end_date, formData.day_of_month, formData.second_day_of_month, formData.custom_interval_days]);

  // Sporadic entry management
  const addSporadicEntry = () => {
    setSporadicEntries(prev => [...prev, {
      id: `entry_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      date: getToday(),
      amount: '',
      is_expense: formData.is_expense
    }]);
  };

  const updateSporadicEntry = (index, field, value) => {
    setSporadicEntries(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const removeSporadicEntry = (index) => {
    setSporadicEntries(prev => prev.filter((_, i) => i !== index));
  };

  const handleOccurrenceAmountChange = (date, value) => {
    setCustomAmounts(prev => ({
      ...prev,
      [date]: value
    }));
  };

  const handleYear2AmountChange = (date, value) => {
    setYear2CustomAmounts(prev => ({
      ...prev,
      [date]: value
    }));
    setYear2WasCustomized(true);
  };

  const resetOccurrenceToDefault = (date) => {
    setCustomAmounts(prev => {
      const next = { ...prev };
      delete next[date];
      return next;
    });
  };

  const resetYear2ToDefault = (date) => {
    setYear2CustomAmounts(prev => {
      const next = { ...prev };
      delete next[date];
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Handle sporadic transactions differently
    if (formData.recurrence_type === 'none') {
      // Validate sporadic entries
      if (sporadicEntries.length === 0) {
        toast.error('Sporadic transactions require at least one custom date/amount entry');
        return;
      }
      
      // Check for duplicate dates
      const dates = sporadicEntries.map(e => e.date);
      const uniqueDates = new Set(dates);
      if (uniqueDates.size !== dates.length) {
        toast.error('Duplicate dates are not allowed. Each date must be unique.');
        return;
      }
      
      // Validate all entries have amounts
      for (let i = 0; i < sporadicEntries.length; i++) {
        const entry = sporadicEntries[i];
        if (!entry.amount || isNaN(parseFloat(entry.amount))) {
          toast.error(`Please enter a valid amount for all dates`);
          return;
        }
      }
      
      // Build custom_amounts object
      const customAmountsPayload = {};
      sporadicEntries.forEach(entry => {
        const amt = parseFloat(entry.amount);
        customAmountsPayload[entry.date] = entry.is_expense ? -Math.abs(amt) : Math.abs(amt);
      });
      
      // Find earliest date for start_date
      const sortedDates = [...dates].sort();
      
      const payload = {
        name: formData.name,
        amount: null,  // Sporadic transactions don't have a default amount
        recurrence_type: 'none',
        start_date: sortedDates[0],
        end_date: null,
        is_hypothetical: formData.is_hypothetical,
        custom_amounts: customAmountsPayload,
      };
      
      onSubmit(payload);
      onOpenChange(false);
      return;
    }
    
    const amount = parseFloat(formData.amount);
    const finalAmount = formData.is_expense ? -Math.abs(amount) : Math.abs(amount);
    
    const payload = {
      name: formData.name,
      amount: finalAmount,
      recurrence_type: formData.recurrence_type,
      start_date: formData.start_date,
      end_date: formData.end_date || null,
      is_hypothetical: formData.is_hypothetical,
    };

    // Add recurrence-specific fields.
    // day_of_week is stored in ISO convention (Mon=0..Sun=6) to match the
    // backend's Python weekday() and DAY_OPTIONS values.
    if (['weekly', 'bi_weekly'].includes(formData.recurrence_type)) {
      if (formData.day_of_week !== null && formData.day_of_week !== undefined) {
        payload.day_of_week = formData.day_of_week;
      } else {
        // Derive from start_date in LOCAL time (avoid UTC parse shifts).
        payload.day_of_week = jsDowToIso(new Date(formData.start_date + 'T00:00:00').getDay());
      }
    }
    if (['monthly', 'quarterly', 'annual'].includes(formData.recurrence_type)) {
      payload.day_of_month = formData.day_of_month || new Date(formData.start_date + 'T00:00:00').getDate();
    }
    if (formData.recurrence_type === 'semi_monthly') {
      payload.day_of_month = formData.day_of_month || 1;
      payload.second_day_of_month = formData.second_day_of_month || 15;
    }
    if (formData.recurrence_type === 'custom') {
      payload.custom_interval_days = formData.custom_interval_days || 7;
    }

    // Include custom amounts in payload for new transactions
    // Merge Year 1 and Year 2 custom amounts, with auto-loop for Year 2 if not customized
    if (!isEditing && (Object.keys(customAmounts).length > 0 || Object.keys(year2CustomAmounts).length > 0)) {
      const signedCustomAmounts = {};
      
      // Add Year 1 custom amounts
      Object.entries(customAmounts).forEach(function(entry) {
        const date = entry[0];
        const amtStr = entry[1];
        const amt = parseFloat(amtStr);
        if (!isNaN(amt)) {
          signedCustomAmounts[date] = formData.is_expense ? -Math.abs(amt) : Math.abs(amt);
        }
      });
      
      // Add Year 2 custom amounts (either customized or auto-looped from Year 1)
      // Use recurrence-aware looping (occurrence-based for bi-weekly/weekly)
      const { year1, year2, occurrencesPerYear: occPerYear } = splitOccurrencesByYear(occurrences, formData.recurrence_type);
      
      year2.forEach(function(y2Occ) {
        // Check if Year 2 was customized for this date
        if (year2CustomAmounts[y2Occ.date]) {
          const amt = parseFloat(year2CustomAmounts[y2Occ.date]);
          if (!isNaN(amt)) {
            signedCustomAmounts[y2Occ.date] = formData.is_expense ? -Math.abs(amt) : Math.abs(amt);
          }
        } else {
          // Auto-loop from Year 1 - use correct looping method based on recurrence type
          const y1Date = getYear1DateForYear2(y2Occ, year1, formData.recurrence_type, occPerYear);
          if (y1Date && customAmounts[y1Date]) {
            const amt = parseFloat(customAmounts[y1Date]);
            if (!isNaN(amt)) {
              signedCustomAmounts[y2Occ.date] = formData.is_expense ? -Math.abs(amt) : Math.abs(amt);
            }
          }
        }
      });
      
      payload.custom_amounts = signedCustomAmounts;
    }

    // Save custom occurrence amounts if editing
    const initialCustom = initialData?.custom_amounts || {};
    const hadCustomAmounts = Object.keys(initialCustom).length > 0;
    if (isEditing && (Object.keys(customAmounts).length > 0 || Object.keys(year2CustomAmounts).length > 0 || hadCustomAmounts)) {
      try {
        const signedCustomAmounts = {};
        const defaultAmount = formData.is_expense ? -Math.abs(formData.amount) : Math.abs(formData.amount);
        
        // For any date that was in the original custom_amounts but has been
        // reset (removed from customAmounts), send the default amount so
        // the backend's merge logic removes it.
        Object.keys(initialCustom).forEach(function(date) {
          if (customAmounts[date] === undefined) {
            signedCustomAmounts[date] = defaultAmount;
          }
        });
        
        // Add Year 1 custom amounts
        Object.entries(customAmounts).forEach(function(entry) {
          const date = entry[0];
          const amtStr = entry[1];
          const amt = parseFloat(amtStr);
          if (!isNaN(amt)) {
            signedCustomAmounts[date] = formData.is_expense ? -Math.abs(amt) : Math.abs(amt);
          }
        });
        
        // Add Year 2 custom amounts (either customized or auto-looped)
        // Use recurrence-aware looping (occurrence-based for bi-weekly/weekly)
        const { year1, year2, occurrencesPerYear: occPerYear } = splitOccurrencesByYear(occurrences, formData.recurrence_type);
        
        year2.forEach(function(y2Occ) {
          if (year2CustomAmounts[y2Occ.date]) {
            const amt = parseFloat(year2CustomAmounts[y2Occ.date]);
            if (!isNaN(amt)) {
              signedCustomAmounts[y2Occ.date] = formData.is_expense ? -Math.abs(amt) : Math.abs(amt);
            }
          } else {
            // Auto-loop from Year 1 using correct method based on recurrence type
            const y1Date = getYear1DateForYear2(y2Occ, year1, formData.recurrence_type, occPerYear);
            if (y1Date && customAmounts[y1Date]) {
              const amt = parseFloat(customAmounts[y1Date]);
              if (!isNaN(amt)) {
                signedCustomAmounts[y2Occ.date] = formData.is_expense ? -Math.abs(amt) : Math.abs(amt);
              }
            }
          }
        });
        
        // Also include custom_amounts in the payload for guest mode
        payload.custom_amounts = signedCustomAmounts;
        
        await updateOccurrenceAmounts(initialData.id, signedCustomAmounts);
      } catch (err) {
        console.error('Failed to save custom amounts:', err);
        // In guest mode the API call fails, but payload.custom_amounts
        // is already set so the guest update handler will persist it.
      }
    }

    onSubmit(payload);
    onOpenChange(false);
  };

  const showDayOfWeek = ['weekly', 'bi_weekly'].includes(formData.recurrence_type);
  const showDayOfMonth = ['monthly', 'quarterly', 'annual', 'semi_monthly'].includes(formData.recurrence_type);
  const showSecondDay = formData.recurrence_type === 'semi_monthly';
  const showCustomInterval = formData.recurrence_type === 'custom';
  const showOccurrences = formData.recurrence_type !== 'one_time' && formData.recurrence_type !== 'none';
  const isSporadic = formData.recurrence_type === 'none';
  
  // Split occurrences into Year 1 and Year 2 for the UI
  // Pass recurrence type for correct bi-weekly/weekly looping
  const { year1: year1Occurrences, year2: year2Occurrences, occurrencesPerYear } = useMemo(() => {
    return splitOccurrencesByYear(occurrences, formData.recurrence_type);
  }, [occurrences, formData.recurrence_type]);

  const customizedCount = Object.keys(customAmounts).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[550px] max-h-[95vh] flex flex-col" data-testid="transaction-form-dialog">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="font-heading text-xl">
            {isEditing ? 'Edit Transaction' : 'Add Transaction'}
          </DialogTitle>
          <DialogDescription>
            {isEditing ? 'Modify the recurring transaction details.' : 'Add a new income or expense to your forecast.'}
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="flex-1 overflow-y-auto pr-2 space-y-4 py-4">
              {/* Name */}
              <div className="space-y-2">
                <Label htmlFor="name">Description</Label>
                <Input
                  id="name"
                  data-testid="transaction-name-input"
                  placeholder="e.g., Rent, Paycheck, Netflix"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>

              {/* Amount and Type - hide for sporadic transactions */}
              {!isSporadic && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="amount">Default Amount</Label>
                    <Input
                      id="amount"
                      data-testid="transaction-amount-input"
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                      onWheel={(e) => e.target.blur()}
                      required
                      className="font-mono"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Type</Label>
                    <div className="flex items-center h-10 gap-3">
                      <span className={formData.is_expense ? 'text-muted-foreground' : 'font-medium text-green-600'}>Income</span>
                      <Switch
                        data-testid="transaction-type-switch"
                        checked={formData.is_expense}
                        onCheckedChange={(checked) => setFormData({ ...formData, is_expense: checked })}
                      />
                      <span className={formData.is_expense ? 'font-medium' : 'text-muted-foreground'}>Expense</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Recurrence Type */}
              <div className="space-y-2">
                <Label>Recurrence</Label>
                <Select
                  value={formData.recurrence_type}
                  onValueChange={(value) => setFormData({ ...formData, recurrence_type: value })}
                >
                  <SelectTrigger data-testid="recurrence-select">
                    <SelectValue placeholder="Select recurrence" />
                  </SelectTrigger>
                  <SelectContent>
                    {RECURRENCE_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Sporadic Transactions - Custom Dates & Amounts */}
              {isSporadic && (
                <div className="space-y-3 border rounded-lg p-4 bg-muted/30">
                  <div className="flex items-center justify-between">
                    <Label className="text-base font-medium">Custom Dates & Amounts</Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addSporadicEntry}
                      data-testid="add-sporadic-entry-btn"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add Date/Amount
                    </Button>
                  </div>
                  
                  {sporadicEntries.length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No entries yet. Click "Add Date/Amount" to add custom occurrences.
                    </p>
                  )}
                  
                  <div className="space-y-2 max-h-[200px] overflow-y-auto">
                    {sporadicEntries.map((entry, index) => (
                        <div key={entry.id || index} className="flex items-center gap-2 bg-background p-2 rounded border">
                          <Input
                            type="date"
                            value={entry.date}
                            onChange={(e) => updateSporadicEntry(index, 'date', e.target.value)}
                            className="w-[140px]"
                            data-testid={`sporadic-date-${index}`}
                          />
                          <div className="flex items-center gap-1">
                            <span className="text-muted-foreground">{currencySymbol}</span>
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              placeholder="0.00"
                              value={entry.amount}
                              onChange={(e) => updateSporadicEntry(index, 'amount', e.target.value)}
                              className="w-[100px] font-mono"
                              data-testid={`sporadic-amount-${index}`}
                            />
                          </div>
                          <div className="flex items-center gap-1 text-xs">
                            <button
                              type="button"
                              onClick={() => updateSporadicEntry(index, 'is_expense', false)}
                              className={`px-2 py-1 rounded ${!entry.is_expense ? 'bg-green-100 text-green-700' : 'text-muted-foreground'}`}
                            >
                              In
                            </button>
                            <button
                              type="button"
                              onClick={() => updateSporadicEntry(index, 'is_expense', true)}
                              className={`px-2 py-1 rounded ${entry.is_expense ? 'bg-red-100 text-red-700' : 'text-muted-foreground'}`}
                            >
                              Out
                            </button>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeSporadicEntry(index)}
                            className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                            data-testid={`remove-sporadic-${index}`}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                  </div>
                  
                  {sporadicEntries.length > 0 && (
                    <p className="text-xs text-muted-foreground">
                      {sporadicEntries.length} occurrence{sporadicEntries.length !== 1 ? 's' : ''} • 
                      Total: {fmtCurrency(sporadicEntries.reduce((sum, e) => {
                        const amt = parseFloat(e.amount) || 0;
                        return sum + (e.is_expense ? -amt : amt);
                      }, 0), currencyCode)}
                    </p>
                  )}
                </div>
              )}

              {/* Recurrence-specific fields */}
              {showDayOfWeek && (
                <div className="space-y-2">
                  <Label>Day of Week</Label>
                  <Select
                    value={formData.day_of_week?.toString() || ''}
                    onValueChange={(value) => setFormData({ ...formData, day_of_week: parseInt(value) })}
                  >
                    <SelectTrigger data-testid="day-of-week-select">
                      <SelectValue placeholder="Select day" />
                    </SelectTrigger>
                    <SelectContent>
                      {DAY_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value.toString()}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {showDayOfMonth && (
                <div className={showSecondDay ? 'grid grid-cols-2 gap-4' : ''}>
                  <div className="space-y-2">
                    <Label>{showSecondDay ? 'First Day' : 'Day of Month'}</Label>
                    <Input
                      data-testid="day-of-month-input"
                      type="number"
                      min="1"
                      max="31"
                      value={formData.day_of_month || ''}
                      onChange={(e) => setFormData({ ...formData, day_of_month: parseInt(e.target.value) || null })}
                      placeholder="1-31"
                    />
                  </div>
                  {showSecondDay && (
                    <div className="space-y-2">
                      <Label>Second Day</Label>
                      <Input
                        data-testid="second-day-input"
                        type="number"
                        min="1"
                        max="31"
                        value={formData.second_day_of_month || ''}
                        onChange={(e) => setFormData({ ...formData, second_day_of_month: parseInt(e.target.value) || null })}
                        placeholder="1-31"
                      />
                    </div>
                  )}
                </div>
              )}

              {showCustomInterval && (
                <div className="space-y-2">
                  <Label>Every X Days</Label>
                  <Input
                    data-testid="custom-interval-input"
                    type="number"
                    min="1"
                    value={formData.custom_interval_days}
                    onChange={(e) => setFormData({ ...formData, custom_interval_days: parseInt(e.target.value) || 1 })}
                  />
                </div>
              )}

              {/* Dates */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="start_date">Start Date</Label>
                  <Input
                    id="start_date"
                    data-testid="start-date-input"
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="end_date">End Date (optional)</Label>
                  <Input
                    id="end_date"
                    data-testid="end-date-input"
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  />
                </div>
              </div>

              {/* Hypothetical toggle */}
              <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                <div>
                  <Label htmlFor="hypothetical" className="font-medium">Hypothetical Transaction</Label>
                  <p className="text-xs text-muted-foreground">Mark as estimated/potential</p>
                </div>
                <Switch
                  id="hypothetical"
                  data-testid="hypothetical-switch"
                  checked={formData.is_hypothetical}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_hypothetical: checked })}
                />
              </div>

              {/* Future Occurrences Editor */}
              {showOccurrences && (
                <Collapsible open={occurrencesOpen} onOpenChange={handleOpenOccurrences}>
                  <CollapsibleTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full justify-between"
                      data-testid="occurrences-toggle"
                    >
                      <span className="flex items-center gap-2">
                        Customize Future Amounts
                        {customizedCount > 0 && (
                          <Badge variant="secondary" className="text-xs">
                            {customizedCount} customized
                          </Badge>
                        )}
                      </span>
                      <ChevronDown className={'w-4 h-4 transition-transform ' + (occurrencesOpen ? 'rotate-180' : '')} />
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="mt-3">
                    <div className="border rounded-lg p-3 bg-muted/30">
                      <p className="text-xs text-muted-foreground mb-3">
                        Override amounts for specific dates (e.g., seasonal bills). Leave blank to use default.
                      </p>
                      {loadingOccurrences ? (
                        <div className="text-center py-4 text-sm text-muted-foreground">
                          Loading future dates...
                        </div>
                      ) : occurrences.length === 0 ? (
                        <div className="text-center py-4 text-sm text-muted-foreground">
                          No future occurrences found.
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {/* Year 1 Section */}
                          <div>
                            <h4 className="text-sm font-medium mb-2">Year 1</h4>
                            <div className="space-y-2 max-h-[200px] overflow-y-auto pr-2" data-testid="year1-occurrences-list">
                              {year1Occurrences.map(function(occ, index) {
                                const hasCustom = customAmounts[occ.date] !== undefined;
                                
                                return (
                                  <div 
                                    key={occ.date} 
                                    data-testid={'year1-occurrence-row-' + index}
                                    className={'flex items-center gap-2 p-2 rounded ' + (hasCustom ? 'bg-purple-500/10 border border-purple-500/30' : 'bg-background')}
                                  >
                                    <span className="text-xs text-muted-foreground w-[140px] flex-shrink-0">
                                      {formatDate(occ.date)}
                                    </span>
                                    <Input
                                      type="number"
                                      step="0.01"
                                      min="0"
                                      placeholder={formatCurrency(Math.abs(initialData?.amount || formData.amount || 0))}
                                      value={hasCustom ? customAmounts[occ.date] : ''}
                                      onChange={function(e) { handleOccurrenceAmountChange(occ.date, e.target.value); }}
                                      onWheel={function(e) { e.target.blur(); }}
                                      className="font-mono h-8 text-sm flex-1"
                                    />
                                    {hasCustom && (
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0"
                                        onClick={function() { resetOccurrenceToDefault(occ.date); }}
                                        title="Reset to default"
                                      >
                                        <RotateCcw className="w-3 h-3" />
                                      </Button>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>

                          {/* Year 2 Auto-Loop Message and Section */}
                          {year2Occurrences.length > 0 && (
                            <>
                              <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
                                <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                                <p className="text-xs text-blue-700 dark:text-blue-300">
                                  Year 2 will automatically repeat this pattern. Click below to customize Year 2 if needed.
                                </p>
                              </div>

                              <Collapsible open={year2Expanded} onOpenChange={setYear2Expanded}>
                                <CollapsibleTrigger asChild>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="w-full justify-between text-muted-foreground hover:text-foreground"
                                  >
                                    <span>Show Year 2 Custom Amounts (optional)</span>
                                    <ChevronDown className={'w-4 h-4 transition-transform ' + (year2Expanded ? 'rotate-180' : '')} />
                                  </Button>
                                </CollapsibleTrigger>
                                <CollapsibleContent className="mt-2">
                                  <div className="space-y-2 max-h-[200px] overflow-y-auto pr-2" data-testid="year2-occurrences-list">
                                    {year2Occurrences.map(function(occ, index) {
                                      // Get corresponding Year 1 value for pre-fill
                                      // Uses occurrence-based mapping for bi-weekly/weekly, month-based for monthly
                                      const y1Date = getYear1DateForYear2(occ, year1Occurrences, formData.recurrence_type, occurrencesPerYear);
                                      const y1Value = y1Date ? customAmounts[y1Date] : null;
                                      const hasCustom = year2CustomAmounts[occ.date] !== undefined;
                                      const displayValue = hasCustom ? year2CustomAmounts[occ.date] : '';
                                      const placeholder = y1Value 
                                        ? `${formatCurrency(parseFloat(y1Value))} (from Year 1)` 
                                        : formatCurrency(Math.abs(initialData?.amount || formData.amount || 0));
                                      
                                      return (
                                        <div 
                                          key={occ.date} 
                                          data-testid={'year2-occurrence-row-' + index}
                                          className={'flex items-center gap-2 p-2 rounded ' + (hasCustom ? 'bg-purple-500/10 border border-purple-500/30' : 'bg-background')}
                                        >
                                          <span className="text-xs text-muted-foreground w-[140px] flex-shrink-0">
                                            {formatDate(occ.date)}
                                          </span>
                                          <Input
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            placeholder={placeholder}
                                            value={displayValue}
                                            onChange={function(e) { handleYear2AmountChange(occ.date, e.target.value); }}
                                            onWheel={function(e) { e.target.blur(); }}
                                            className="font-mono h-8 text-sm flex-1"
                                          />
                                          {hasCustom && (
                                            <Button
                                              type="button"
                                              variant="ghost"
                                              size="sm"
                                              className="h-8 w-8 p-0"
                                              onClick={function() { resetYear2ToDefault(occ.date); }}
                                              title="Reset to Year 1 value"
                                            >
                                              <RotateCcw className="w-3 h-3" />
                                            </Button>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                </CollapsibleContent>
                              </Collapsible>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              )}
            </div>

          <DialogFooter className="flex-shrink-0 mt-4 pt-4 border-t sm:justify-between">
            {isEditing && onDelete ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowDeleteConfirm(true)}
                data-testid="delete-transaction-btn"
                className="text-destructive border-destructive/40 hover:bg-destructive/10 hover:text-destructive"
              >
                <Trash2 className="w-4 h-4 mr-2" /> Delete Transaction
              </Button>
            ) : (
              // Empty span keeps the Save/Cancel pair right-aligned via
              // justify-between when the Delete button is absent (create mode).
              <span />
            )}
            <div className="flex items-center gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="submit-transaction-btn">
                {isEditing ? 'Save Changes' : 'Add Transaction'}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>

      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {isRecurring ? 'Delete recurring transaction?' : 'Delete this transaction?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {isRecurring ? (
                <>
                  This will permanently delete{' '}
                  <strong>{initialData?.name || 'this transaction'}</strong> and{' '}
                  <strong>all of its future occurrences</strong> from your forecast.
                  <br /><br />
                  If you only want to skip a single occurrence, cancel here — then customize
                  that specific date&rsquo;s amount to <strong>0</strong> in the &ldquo;Customize
                  Future Amounts&rdquo; section instead of deleting the whole transaction.
                </>
              ) : (
                <>
                  This will permanently delete{' '}
                  <strong>{initialData?.name || 'this transaction'}</strong> from your forecast.
                  This action cannot be undone.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-transaction-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              data-testid="delete-transaction-confirm-btn"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isRecurring ? 'Delete all occurrences' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
};
